import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BsChevronDown } from 'react-icons/bs';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  Button, Divider, Flex, Icon,
  Popover, PopoverBody, PopoverContent,
  PopoverHeader, PopoverTrigger, Text,
  useDisclosure
} from '@chakra-ui/react';
import { useSelect } from 'downshift';
import PropTypes from 'prop-types';

import { menuItemBaseStyle } from '../../styles/theme/components/menu';
import colors from '../../styles/theme/foundation/colors';
import { activeStyle, hoverStyle, popoverMenuButtonStyles } from '../../styles/theme/util/popoverMenuButtonStyles';
import AddCustomInput from '../DropdownMenu/AddCustomInput';
import SearchGlobalSearchInput from '../search/SearchGlobalSearchInput';
import SearchInputGroup from '../SearchInputGroup';
import TruncationTooltip from '../TruncationTooltip';

const SearchableDropdownMenu = ({
  isDisabled,
  selected,
  onSelect,
  searchBlueprint,
  hasMore,
  fetchNextData,
  showError = false,
  truncateMenuItems = true,
  allowCustomValues = false,
  customPlaceholder = '',
  customLabel = 'New Option',
  customValidation = () => {},
  onCustomSelect = () => {},
  width = '300px',
  maxWidth = width,
  minWidth = width,
  matchWidth = false,
  data = [],
  labelKey = 'label',
  idKey = 'id',
  placeholder = 'None Selected',
  searchTerm: passedSearchTerm = '',
  onSearchChange = () => {},
  endMessage = '',
  dataAnalyticsKey = undefined,
  duskSelector = undefined,
  cypressId = undefined
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const menuRef = useRef();
  const searchInputRef = useRef();

  const initialItems = useMemo(() => data.map(item => ({
    original: item,
    value: item[idKey],
    label: typeof labelKey === 'function' ? labelKey(item) : item[labelKey],
    dataAnalyticsId: item?.dataAnalyticsId || (
      dataAnalyticsKey ? `${dataAnalyticsKey}-item` : undefined
    )
  })), [data]);

  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState(passedSearchTerm);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  useEffect(() => {
    setSearchTerm(passedSearchTerm);
  }, [passedSearchTerm]);

  const getButtonStyle = () => {
    let style = { ...popoverMenuButtonStyles, width, color: colors.greyScale['500'] };
    if (selected) style = { ...popoverMenuButtonStyles, color: colors.greyScale['900'], width };
    if (showError) style = { ...popoverMenuButtonStyles, boxShadow: `inset 0 0 0 1px ${colors.error.primary['600']}` };
    if (isOpen) {
      style = {
        ...popoverMenuButtonStyles,
        width,
        color: colors.primaryScale['600'],
        borderColor: colors.primaryScale['600'],
        boxShadow: `inset 0 0 0 1px ${colors.primaryScale['600']}`
      };
    }

    return style;
  };

  const {
    getToggleButtonProps,
    getMenuProps,
    getItemProps
  } = useSelect({
    items,
    onSelectedItemChange: ({ selectedItem }) => {
      onSelect(selectedItem.original);
      onClose();
    }
  });

  const overflowStyles = useMemo(() => (truncateMenuItems
    ? { height: '40px' } : { height: 'auto', minHeight: '40px', overflowWrap: 'wrap', whiteSpace: 'wrap' }), []);

  const displayName = useMemo(() => {
    if (selected && selected !== 'undefined') {
      return selected;
    }

    return placeholder;
  }, [selected, placeholder]);

  return (
    <Popover
      closeOnBlur
      initialFocusRef={searchInputRef}
      isOpen={isOpen}
      matchWidth={matchWidth}
      returnFocusOnClose={false}
      onClose={onClose}
      onOpen={onOpen}
    >
      <PopoverTrigger>
        <Button
          _active={activeStyle}
          _hover={hoverStyle}
          data-analytics-id={dataAnalyticsKey}
          data-cy={cypressId}
          dusk={duskSelector}
          isDisabled={isDisabled}
          style={getButtonStyle()}
          variant="unstyled"
          width={width}
          {...getToggleButtonProps()}
        >
          <Flex
            alignItems="center"
            justify="space-between"
            pointerEvents="none"
            width="100%"
          >
            <TruncationTooltip label={displayName}>
              <Text noOfLines={1} pointerEvents="auto" width="90%">
                {displayName}
              </Text>
            </TruncationTooltip>
            <Icon as={BsChevronDown} color={isOpen ? 'inherit' : colors.greyScale['500']} />
          </Flex>
        </Button>
      </PopoverTrigger>
      <PopoverContent maxH={350} width={width}>
        <PopoverHeader as={Flex} justifyContent="flex-end" p={2} width={width}>
          {searchBlueprint
            ? (
              <SearchGlobalSearchInput
                cypressId={`${cypressId}-search-input`}
                dataAnalyticsId={`${dataAnalyticsKey}-search-input`}
                flex="1"
                ref={searchInputRef}
                searchBlueprint={searchBlueprint}
                width="100%"
              />
            )
            : (
              <SearchInputGroup
                cypressId={`${cypressId}-search-input`}
                dataAnalyticsId={`${dataAnalyticsKey}-search-input`}
                ref={searchInputRef}
                searchTerm={searchTerm}
                variant="primary"
                width="100%"
                onChange={e => {
                  onSearchChange(e.target.value);
                  setSearchTerm(e.target.value);
                }}
              />
            )}
        </PopoverHeader>
        <PopoverBody maxWidth={maxWidth} minWidth={minWidth} width={width}>
          <ul {...getMenuProps({ ref: menuRef, style: { listStyleType: 'none', margin: 0, padding: 0 } })}>
            <InfiniteScroll
              dataLength={data.length}
              endMessage={endMessage}
              hasMore={hasMore}
              height={items.length < 6 ? 'auto' : 225}
              loader={(
                <Text color="greyScale.500" pt={2} textAlign="center" textStyle="bodySm">
                  Loading...
                </Text>
            )}
              next={fetchNextData}
              overflowX="hidden"
            >
              {items.length
                ? items.map((item, index) => (
                  <TruncationTooltip
                    isDisabled={!truncateMenuItems}
                    key={item.value}
                    label={item.label}
                  >
                    <Button
                      _hover={{
                        cursor: 'pointer',
                        bgColor: colors.primaryScale['50'],
                        color: colors.primaryScale['600']
                      }}
                      _selected={menuItemBaseStyle._focus}
                      borderRadius="md"
                      color={colors.greyScale['600']}
                      data-analytics-id={item?.dataAnalyticsId}
                      data-cy={`${cypressId}-item_id-${item.value}`}
                      dusk={`${duskSelector}-item_id-${item.value}`}
                      id={item.value}
                      padding="10px 14px"
                      size="md"
                      textAlign="left"
                      textStyle="bodyMd500"
                      value={item.value}
                      variant="unstyled"
                      width="100%"
                      {...(truncateMenuItems ? { noOfLines: 1 } : {})}
                      {...overflowStyles}
                      {...getItemProps({ item, index })}
                    >
                      {item.label}
                    </Button>
                  </TruncationTooltip>
                )) : <Text color={colors.greyScale['500']} textStyle="bodyMd">No results</Text>}
            </InfiniteScroll>
          </ul>
        </PopoverBody>
        {(allowCustomValues && items.length < 10) && (
        <>
          <Divider />
          <AddCustomInput
            buttonLabel={customLabel}
            customValidation={customValidation}
            placeholder={customPlaceholder}
            onClose={onClose}
            onCustomSelect={onCustomSelect}
          />
        </>
        )}
      </PopoverContent>
    </Popover>
  );
};

SearchableDropdownMenu.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.arrayOf(PropTypes.shape({})),
  labelKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  idKey: PropTypes.string,
  selected: PropTypes.string,
  onSelect: PropTypes.func,
  searchBlueprint: PropTypes.shape({}),
  searchTerm: PropTypes.string,
  onSearchChange: PropTypes.func,
  hasMore: PropTypes.bool,
  endMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  fetchNextData: PropTypes.func,
  dataAnalyticsKey: PropTypes.string,
  isDisabled: PropTypes.bool,
  truncateMenuItems: PropTypes.bool,
  allowCustomValues: PropTypes.bool,
  customPlaceholder: PropTypes.string,
  customLabel: PropTypes.string,
  customValidation: PropTypes.func,
  onCustomSelect: PropTypes.func,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  matchWidth: PropTypes.bool,
  placeholder: PropTypes.string
};

export default SearchableDropdownMenu;
